import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  getCriteriaTextSuggestion,
  getCriteriaTitle,
  getCriteriaType,
} from '@app/library/components/sides/library-create-playlist/library-create-playlist.component';
import { criteria } from '@app/library/models/criterias.model';
import { PlaylistApi } from '@app/library/models/playlist-api.model';
import { Playlist } from '@app/library/models/playlist.model';
import {
  EditPlaylistDynamicFailure,
  EditPlaylistDynamicRequest,
  EditPlaylistDynamicSuccess,
  EditPlaylistManualFailure,
  EditPlaylistManualRequest,
  EditPlaylistManualSuccess,
  EndEditPlaylist,
  GetPlaylistFailure,
  GetPlaylistRequest,
  GetPlaylistSuccess,
  InvalidCriteria,
  SetActionAfterEditPlaylist,
} from '@app/library/states/playlists.actions';
import { PlaylistsState } from '@app/library/states/playlists.state';
import { emptyCriterionValidator } from '@app/shared/custom-validators';
import { isEqualFormData, mSanitize } from '@app/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import {
  ErrorFormFormat,
  FormHelperService,
  UiStoreHelperService,
} from '@radioking/shared/common-services';
import {
  DynamicEnabledPanel,
  TryActionUnlessPanelIsBlocking,
} from '@radioking/shared/pannel-manager';
import { SugestionInput } from '@radioking/ui/input';
import deepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'rk-library-edit-playlist',
  templateUrl: './library-edit-playlist.component.html',
  styleUrls: ['./library-edit-playlist.component.scss'],
})
export class LibraryEditPlaylistComponent
  implements OnInit, OnDestroy, DynamicEnabledPanel
{
  editPlaylistForm: UntypedFormGroup;
  editPlaylistFormErrors: ErrorFormFormat;
  initialFormData: Partial<Playlist> = {};

  @Select(PlaylistsState.editingPlaylist)
  playlist$: Observable<Playlist>;

  isLoading$: Observable<boolean>;
  isSaving$: Observable<boolean>;

  modalTitle = '';

  submitSubject: Subject<void> = new Subject<void>();

  subscription = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly formHelper: FormHelperService,
    private readonly fb: UntypedFormBuilder,
    private readonly helper: UiStoreHelperService,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.subscription.add(
      this.playlist$
        .pipe(filter(pl => !!pl))
        .subscribe(playlist => this.createForm(playlist)),
    );

    const playlistType = this.store.selectSnapshot(PlaylistsState.editingPlaylistType);
    this.isLoading$ = this.helper.getLoadingState(
      GetPlaylistRequest,
      [GetPlaylistSuccess, GetPlaylistFailure],
      playlistType === 'dynamic',
    );
    this.isSaving$ = this.helper.getLoadingState(
      [EditPlaylistManualRequest, EditPlaylistDynamicRequest],
      [
        EditPlaylistManualSuccess,
        EditPlaylistManualFailure,
        EditPlaylistDynamicSuccess,
        EditPlaylistDynamicFailure,
      ],
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  hasToShowConfirmOnExit(): boolean {
    return true;
  }

  isDirty(): boolean {
    return (
      this.editPlaylistForm &&
      !this.editPlaylistForm.pristine &&
      !isEqualFormData(this.editPlaylistForm.getRawValue(), this.initialFormData)
    );
  }

  get criteria() {
    return this.editPlaylistForm.get('criteria') as UntypedFormArray;
  }

  createForm(playlist: Playlist) {
    this.modalTitle = playlist.name;
    this.editPlaylistFormErrors = {
      name: {},
      color: {},
    };
    this.editPlaylistForm = this.fb.group({
      name: [playlist.name, Validators.required],
      color: [playlist.color, Validators.required],
      criteria: playlist.criteria
        ? this.fb.array(playlist.criteria, emptyCriterionValidator())
        : null,
    });
    this.initialFormData = cloneDeep({ ...this.editPlaylistForm.getRawValue() });
    const initialForm = this.editPlaylistForm.get('criteria').value;

    this.subscription.add(
      this.editPlaylistForm.valueChanges.subscribe(val => {
        if (deepEqual(val.criteria, initialForm)) {
          this.editPlaylistForm.get('criteria').markAsPristine();
        }
        this.formHelper.updateErrorMapping(
          this.editPlaylistForm,
          this.editPlaylistFormErrors,
        );
      }),
    );
    this.formHelper.updateErrorMapping(
      this.editPlaylistForm,
      this.editPlaylistFormErrors,
    );
  }

  addCriteria(field: string) {
    this.criteria.push(this.fb.control({ field, operator: 'equals', value: '' }));
    setTimeout(() => {
      const sideNav: HTMLElement = document.querySelector(
        'rk-sidenav-action-wrapper .content',
      );
      sideNav.style.scrollBehavior = 'smooth';
      sideNav.scrollTop = 9999;
      sideNav.style.scrollBehavior = 'auto';
    }, 200);
  }

  removeCriteria(index: number) {
    this.criteria.removeAt(index);
    this.editPlaylistForm.get('criteria').markAsDirty();
  }

  getCriteriaType(field: string): string {
    return getCriteriaType(field);
  }

  getCriteriaTitle(field: string): string {
    return getCriteriaTitle(field, this.translateService);
  }

  getCriteriaTextSuggestion(field: criteria): SugestionInput[] {
    return getCriteriaTextSuggestion(field, this.store);
  }

  @Dispatch()
  exitPanel = () => new TryActionUnlessPanelIsBlocking(new EndEditPlaylist());

  @Dispatch()
  cancelEdit = () => new EndEditPlaylist();

  @Dispatch()
  invalidCriteria = () => new InvalidCriteria();

  submit() {
    const currentPlaylist = this.store.selectSnapshot(PlaylistsState.editingPlaylist);
    if (
      currentPlaylist.type === 'dynamic' &&
      !this.editPlaylistForm.get('criteria').pristine
    ) {
      this.submitSubject.next();
      if (this.editPlaylistForm.invalid) {
        this.invalidCriteria();

        return;
      }
      this.editDynamicPlaylist(currentPlaylist.id);

      return;
    }
    this.editManualPlaylist(currentPlaylist.id);
  }

  @Dispatch()
  editManualPlaylist = (playlistId: number) => {
    const formValues = this.editPlaylistForm.value;
    const playlist: Partial<PlaylistApi> = {
      name: mSanitize(formValues.name),
      color: formValues.color,
    };

    return new EditPlaylistManualRequest(playlistId, playlist);
  };

  @Dispatch()
  private readonly editDynamicPlaylist = (playlistId: number) => {
    const formValues = this.editPlaylistForm.value;
    const playlist: Partial<PlaylistApi> = {
      name: mSanitize(formValues.name),
      color: formValues.color,
      type: 'dynamic',
      criteria: formValues.criteria,
    };

    return new EditPlaylistDynamicRequest(playlistId, playlist);
  };

  closeAction(action: any): void {
    this.store.dispatch(new SetActionAfterEditPlaylist(action));
    this.submit();
  }

  canSave(): boolean {
    return true;
  }

  getCriteriaLength(): number {
    return (this.editPlaylistForm.get('criteria') as UntypedFormArray).length;
  }
}
